if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      // BJP-316 — no-selection guard. The PDP no longer pre-selects a size, so
      // the CTA can be pressed before a variant exists. `data-pd-needs-size` is
      // set in Liquid (snippets/pd-product-info.liquid) and cleared by the PDP's
      // inline script once every option group has a value.
      //
      // Deliberately not the `disabled` attribute: `disabled` means SOLD OUT
      // theme-wide and would swap the CTA for the back-in-stock button. The
      // button therefore stays live and this is the thing that stops the add.
      //
      // Scoped by the attribute, so every other <product-form> in the theme
      // (quick-add, cart upsell, cross-sell) is untouched.
      if (this.submitButton.hasAttribute('data-pd-needs-size')) {
        this.showSelectionPrompt();
        return;
      }

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            // BIL-401 — hand the raw payload to anything that can render a
            // better message than the generic banner. /cart/add.js rejects a
            // gift card with a bad recipient using a 422 whose `errors` map is
            // keyed per field (`email`, `name`, `message`, `send_on`), which
            // <pd-gift-card-recipient> turns into inline field errors.
            // Mirrors the existing `pd:product-added` announcement below; every
            // consumer does its own gating.
            document.dispatchEvent(new CustomEvent('pd:cart-error', {
              detail: { productForm: this, variantId: formData.get('id'), response }
            }));

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }

          // BJP-276 — announce every successful add so features can react
          // (e.g. the PDP "You may also need" popup). Consumers do their own
          // gating; see frontend/components/pd-upsell-popup.js.
          document.dispatchEvent(new CustomEvent('pd:product-added', {
            detail: { productForm: this, variantId: formData.get('id') }
          }));
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    /* BJP-316 — reveal the "Please select a size" prompt and take the shopper to
     * the picker. The prompt element is rendered hidden next to the CTA by
     * snippets/pd-product-info.liquid and referenced by the button's
     * aria-describedby, so unhiding it both shows the red line and gives the
     * button an accessible description.
     *
     * The scroll matters for the mobile sticky bar (snippets/pd-sticky-atc.liquid):
     * its CTA submits this same form from the bottom of the viewport, where the
     * size pills — and the message — are far off-screen. Without moving the
     * viewport the tap would look like it did nothing at all. */
    showSelectionPrompt() {
      const promptId = this.submitButton.getAttribute('aria-describedby');
      const prompt = promptId ? document.getElementById(promptId) : null;
      if (prompt) prompt.hidden = false;

      const firstUnchosen = Array.from(this.querySelectorAll('variant-radios fieldset'))
        .find((fieldset) => !fieldset.querySelector('input[type="radio"]:checked'));
      if (!firstUnchosen) return;

      firstUnchosen.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus the first pill so keyboard and screen-reader users land on the
      // control they need to operate. `preventScroll` leaves the smooth scroll
      // above in charge instead of letting focus jump the viewport instantly.
      firstUnchosen.querySelector('input[type="radio"]')?.focus({ preventScroll: true });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
